import {
  ItemPreferencesName,
  ItemType,
  TeethShadeType,
  WaxUpTypeFr,
  type AnatomyItem,
  type fullDentureArcadeData,
  type ImplantItem,
  type Product,
  type RemovableItem,
  getDentureMaterialLabel,
  getDentureOrderTypeLabel,
  getDentureShadeLabel,
  getDentureShadeTypeLabel,
  getGingivaShadeLabel,
  getImprintTypeLabel,
  getPartialDentureArchPreferences,
  getPartialDentureOptionLabels,
  getTeethShapeLabel,
  getTeethToRemove,
  getWorkflowTypeLabel,
  getImplantRetentionTypeName,
  getItemAbutmentMaterialLabel,
  getItemAbutmentTypeLabel,
  getItemInlayCoreHasClavetteLabel,
  getItemInlayCoreMaterialLabel,
  getItemInlayCoreScanPostName,
  getItemMaterialLabel,
  getItemShadeLabel,
  getTeethSideLabel,
  isAllMissingTeeth,
  sortItems,
  getArchFromTeeth,
  getItemTypeLabel,
  isRemovableItemType,
  getItemPreferenceLabel,
  getItemPreferenceValues,
} from "@jasper/shared";

type ProductItem = AnatomyItem | ImplantItem | RemovableItem;

export type ProductDetail = { label: string; value: string };
export type ProductDetailGroup = { title?: string; details: ProductDetail[] };

type ItemDetails = {
  itemType: ItemType;
  teeth: number[];
  details: ProductDetail[];
};

const SUMMARY_SEPARATOR = " · ";
const MULTI_SHADE_LABEL = "Teinte multiple";

const INLAY_CORE_ITEM_TYPES: ItemType[] = [
  ItemType.INLAY_CORE,
  ItemType.POST_AND_CORE_RETAINED_CROWN,
  ItemType.RICHMOND_CROWN,
];

const DENTURE_PRODUCT_TYPES: ItemType[] = [
  ItemType.PARTIAL_DENTURE,
  ItemType.FULL_DENTURE,
];

const PREFERENCE_NAMES = Object.values(ItemPreferencesName);

const isPontic = (item: ProductItem): boolean =>
  item.itemType === ItemType.BRIDGE_PONTIC;

const getProductItems = (product: Product): ProductItem[] => [
  ...(product.anatomyItem ?? []),
  ...(product.implantItem ?? []),
  ...(product.removableItem ?? []),
];

const sortTeeth = (teeth: number[]): number[] =>
  [...new Set(teeth)].sort((a, b) => a - b);

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const lowerFirst = (value: string): string =>
  value.charAt(0).toLowerCase() + value.slice(1);

const uniqueJoin = (values: (string | false | null | undefined)[]): string =>
  [...new Set(values.filter((value): value is string => !!value))].join(", ");

const toDetails = (
  label: string,
  value: string | false | null | undefined
): ProductDetail[] => (value ? [{ label, value }] : []);

const getMultiShadeInfo = (item: ProductItem) =>
  "multiShadeInfo" in item ? (item.multiShadeInfo ?? []) : [];

const isMultiShade = (item: ProductItem): boolean =>
  ("teethshadeType" in item &&
    item.teethshadeType === TeethShadeType.MULTI_SHADE) ||
  getMultiShadeInfo(item).length > 0;

const getMaterial = (item: ProductItem): string | undefined =>
  item.itemType === ItemType.INLAY_CORE
    ? undefined
    : getItemMaterialLabel(item);

const getShade = (item: ProductItem): string | undefined => {
  if (item.itemType === ItemType.INLAY_CORE) {
    return undefined;
  }
  return isMultiShade(item) ? MULTI_SHADE_LABEL : getItemShadeLabel(item);
};

const getInlayCore = (item: ProductItem): string | undefined =>
  "inlayCoreMaterial" in item && INLAY_CORE_ITEM_TYPES.includes(item.itemType)
    ? [
        getItemInlayCoreMaterialLabel(item),
        getItemInlayCoreHasClavetteLabel(item),
      ]
        .filter(Boolean)
        .join(" ")
    : undefined;

const getWaxUpType = (item: ProductItem): string | undefined =>
  item.itemType === ItemType.WAX_UP && "waxUpType" in item
    ? WaxUpTypeFr[item.waxUpType]
    : undefined;

const SUMMARY_PARTS = [getMaterial, getShade, getInlayCore, getWaxUpType];

const getItemsSummary = (product: Product): string => {
  const items = getProductItems(product).filter(item => !isPontic(item));

  return SUMMARY_PARTS.map(getPart => uniqueJoin(items.map(getPart)))
    .filter(Boolean)
    .join(SUMMARY_SEPARATOR);
};

const getShadeDetails = (item: ProductItem): ProductDetail[] => {
  const multiShadeInfo = getMultiShadeInfo(item);

  return multiShadeInfo.length > 0
    ? multiShadeInfo.map(({ teethShadeSide, itemShade }) => ({
        label: getTeethSideLabel(teethShadeSide),
        value: itemShade.label,
      }))
    : toDetails("Teinte", getItemShadeLabel(item));
};

const getInlayCoreDetails = (item: AnatomyItem): ProductDetail[] =>
  INLAY_CORE_ITEM_TYPES.includes(item.itemType)
    ? [
        ...toDetails(
          item.itemType === ItemType.INLAY_CORE
            ? "Matériau de l'inlay-core"
            : "Matériau du tenon",
          item.inlayCoreMaterial?.label
        ),
        ...toDetails("Clavette", item.inlayCoreHasClavette && "Oui"),
        ...toDetails(
          "Scan post",
          !!item.inlayCoreScanPost && getItemInlayCoreScanPostName(item)
        ),
      ]
    : [];

const getAnatomyItemDetails = (item: AnatomyItem): ProductDetail[] => [
  ...(item.itemType === ItemType.INLAY_CORE
    ? []
    : [
        ...toDetails("Matériau", getItemMaterialLabel(item)),
        ...getShadeDetails(item),
      ]),
  ...getInlayCoreDetails(item),
];

const getImplantItemDetails = (item: ImplantItem): ProductDetail[] => [
  ...toDetails("Matériau", getItemMaterialLabel(item)),
  ...getShadeDetails(item),
  ...toDetails("Marque d'implant", item.implantReference?.brand),
  ...toDetails("Référence d'implant", item.implantReference?.label),
  ...toDetails("Référence d'implant suggérée", item.implantReferenceSuggestion),
  ...toDetails("Plateforme", item.implantPlatform),
  ...toDetails("Scan body", item.scanBodySuggestion),
  ...toDetails("Type de pilier", getItemAbutmentTypeLabel(item)),
  ...toDetails("Pilier standard suggéré", item.standardAbutmentSuggestion),
  ...toDetails("Matériau du pilier", getItemAbutmentMaterialLabel(item)),
  ...toDetails(
    "Système de rétention",
    capitalize(getImplantRetentionTypeName(item))
  ),
];

const getRemovableItemDetails = (item: RemovableItem): ProductDetail[] => [
  ...toDetails("Matériau", getItemMaterialLabel(item)),
  ...toDetails(
    "Type de wax-up",
    item.itemType === ItemType.WAX_UP && WaxUpTypeFr[item.waxUpType]
  ),
  ...toDetails("Teinte", getItemShadeLabel(item)),
  ...toDetails("Protocole", getWorkflowTypeLabel(item.workflowType)),
];

const describeItems = <T extends ProductItem>(
  items: T[] | undefined,
  productType: ItemType,
  getDetails: (item: T) => ProductDetail[]
): ItemDetails[] => {
  const sortedItems: T[] = sortItems(items ?? [], productType);

  return sortedItems
    .filter(item => !isPontic(item))
    .map(item => ({
      itemType: item.itemType,
      teeth: item.teeth ?? [],
      details: getDetails(item),
    }));
};

const getItemGroups = (product: Product): ItemDetails[] => {
  const groups = new Map<string, ItemDetails>();

  [
    ...describeItems(
      product.anatomyItem,
      product.productType,
      getAnatomyItemDetails
    ),
    ...describeItems(
      product.implantItem,
      product.productType,
      getImplantItemDetails
    ),
    ...describeItems(
      product.removableItem,
      product.productType,
      getRemovableItemDetails
    ),
  ].forEach(item => {
    const key = JSON.stringify(item.details);
    const group = groups.get(key);
    if (group) {
      group.teeth = [...group.teeth, ...item.teeth];
    } else {
      groups.set(key, { ...item });
    }
  });

  return [...groups.values()].filter(group => group.details.length > 0);
};

const getTeethDetails = (product: Product): ProductDetail[] => {
  const items = getProductItems(product);

  if (isRemovableItemType(product.productType)) {
    return toDetails(
      "Arcade",
      capitalize(getArchFromTeeth(items.flatMap(item => item.teeth ?? [])))
    );
  }

  const ponticTeeth = sortTeeth(
    items.filter(isPontic).flatMap(item => item.teeth ?? [])
  );
  const teeth = sortTeeth(
    items.filter(item => !isPontic(item)).flatMap(item => item.teeth ?? [])
  ).filter(tooth => !ponticTeeth.includes(tooth));
  const value = [
    teeth.join(", "),
    ponticTeeth.length > 0 && `pontique ${ponticTeeth.join(", ")}`,
  ]
    .filter(Boolean)
    .join(SUMMARY_SEPARATOR);

  return toDetails(
    teeth.length + ponticTeeth.length > 1 ? "Dents" : "Dent",
    value
  );
};

const getPreferenceDetails = (product: Product): ProductDetail[] => {
  if (product.productType === ItemType.INLAY_CORE) {
    return [];
  }
  const preferences = getItemPreferenceValues(product.itemPreferences);

  return PREFERENCE_NAMES.filter(name => !!preferences[name]).map(name =>
    getItemPreferenceLabel(name, preferences, false)
  );
};

const getItemsDetailGroups = (product: Product): ProductDetailGroup[] => {
  const teethDetails = getTeethDetails(product);
  const productDetails = [
    ...toDetails("Forme des dents", product.itemPreferences?.set?.teethShape),
    ...getPreferenceDetails(product),
  ];
  const itemGroups = getItemGroups(product);

  const groups: ProductDetailGroup[] =
    itemGroups.length > 1
      ? [
          { details: [...teethDetails, ...productDetails] },
          ...itemGroups.map(({ itemType, teeth, details }) => ({
            title: `${getItemTypeLabel(itemType)} · ${sortTeeth(teeth).join(", ")}`,
            details,
          })),
        ]
      : [
          {
            details: [
              ...teethDetails,
              ...(itemGroups[0]?.details ?? []),
              ...productDetails,
            ],
          },
        ];

  return groups.filter(group => group.details.length > 0);
};

const isDenture = (product: Product): boolean =>
  DENTURE_PRODUCT_TYPES.includes(product.productType);

const isPartialDenture = (item: RemovableItem): boolean =>
  item.itemType === ItemType.PARTIAL_DENTURE;

const getDentureProtocol = (item: RemovableItem): string =>
  getDentureOrderTypeLabel(
    item.partialDentureOrderType ?? item.fullDentureOrderType,
    item
  ) || getWorkflowTypeLabel(item.workflowType);

const countLabel = (count: number, singular: string, plural: string) =>
  `${count} ${count > 1 ? plural : singular}`;

const getPartialDentureTeethSummary = (
  item: RemovableItem
): string | undefined => {
  const missingTeeth = item.partialDenture?.missingTeeth;
  if (!missingTeeth || isAllMissingTeeth(item)) {
    return undefined;
  }
  const extractedTeeth = getTeethToRemove(item);

  return [
    missingTeeth.length > 0 &&
      countLabel(missingTeeth.length, "dent remplacée", "dents remplacées"),
    extractedTeeth.length > 0 &&
      countLabel(extractedTeeth.length, "dent extraite", "dents extraites"),
  ]
    .filter(Boolean)
    .join(", ");
};

const getFullDentureArches = (
  product: Product
): { title: string; data?: fullDentureArcadeData }[] => {
  const arcadeData = product.itemPreferences?.fullDentureArcadeData;

  return arcadeData
    ? [
        { title: "Arcade maxillaire", data: arcadeData.maxillaire },
        { title: "Arcade mandibulaire", data: arcadeData.mandibulaire },
      ]
    : [];
};

const getDentureSummary = (product: Product): string => {
  const item = product.removableItem?.[0];
  if (!item) {
    return "";
  }
  const arches = getFullDentureArches(product);
  const materials =
    arches.length > 0
      ? arches.map(
          ({ data }) =>
            data?.itemMaterialId && getDentureMaterialLabel(data.itemMaterialId)
        )
      : [item.itemMaterial && getDentureMaterialLabel(item.itemMaterial.id)];
  const shades =
    arches.length > 0
      ? arches.map(
          ({ data }) =>
            data?.teethShadeId && getDentureShadeLabel(data.teethShadeId)
        )
      : [item.teethShade && getDentureShadeLabel(item.teethShade.id)];
  const shade = uniqueJoin(shades);

  return [
    uniqueJoin(materials),
    shade && `dents ${shade}`,
    isPartialDenture(item) && getPartialDentureTeethSummary(item),
    lowerFirst(getDentureProtocol(item)),
  ]
    .filter(Boolean)
    .join(SUMMARY_SEPARATOR);
};

const getPartialDentureTeethDetails = (
  item: RemovableItem
): ProductDetail[] => {
  const missingTeeth = item.partialDenture?.missingTeeth;
  if (!missingTeeth) {
    return toDetails("Dents", (item.teeth ?? []).join(", "));
  }
  if (isAllMissingTeeth(item)) {
    return toDetails("Dents à remplacer", "Toutes les dents manquantes");
  }
  return [
    ...toDetails("Dents à remplacer", missingTeeth.join(", ")),
    ...toDetails("Dents à extraire", getTeethToRemove(item).join(", ")),
  ];
};

const getDentureAestheticsDetails = (
  item: RemovableItem,
  product: Product
): ProductDetail[] => {
  const { shadeType, teethShape } = getPartialDentureArchPreferences(
    item,
    product
  );

  return [
    ...toDetails(
      "Teinte des dents",
      item.teethShade && getDentureShadeLabel(item.teethShade.id)
    ),
    ...toDetails(
      "Type de teinte",
      shadeType && getDentureShadeTypeLabel(shadeType)
    ),
    ...toDetails(
      "Teinte de la gencive",
      item.gingivaShade && getGingivaShadeLabel(item.gingivaShade.id)
    ),
    ...toDetails(
      "Forme des dents",
      teethShape && getTeethShapeLabel(teethShape)
    ),
  ];
};

const getFullDentureArchGroups = (product: Product): ProductDetailGroup[] =>
  getFullDentureArches(product).map(({ title, data }) => ({
    title,
    details: [
      ...toDetails(
        "Matériau",
        data?.itemMaterialId && getDentureMaterialLabel(data.itemMaterialId)
      ),
      ...toDetails(
        "Teinte des dents",
        data?.teethShadeId && getDentureShadeLabel(data.teethShadeId)
      ),
      ...toDetails(
        "Type de teinte",
        data?.shadeType && getDentureShadeTypeLabel(data.shadeType)
      ),
      ...toDetails(
        "Teinte de la gencive",
        data?.gingivaShadeId && getGingivaShadeLabel(data.gingivaShadeId)
      ),
      ...toDetails(
        "Forme des dents",
        data?.teethShape && getTeethShapeLabel(data.teethShape)
      ),
    ],
  }));

const getDentureItemGroups = (
  item: RemovableItem,
  product: Product,
  titleSuffix: string,
  extraDetails: ProductDetail[]
): ProductDetailGroup[] => {
  const archGroups = isPartialDenture(item)
    ? []
    : getFullDentureArchGroups(product);
  const prosthesisDetails = [
    ...toDetails("Arcade", capitalize(getArchFromTeeth(item.teeth ?? []))),
    ...(isPartialDenture(item) ? getPartialDentureTeethDetails(item) : []),
    ...toDetails("Protocole", getDentureProtocol(item)),
    ...toDetails("Type d'empreinte", getImprintTypeLabel(item)),
    ...(archGroups.length > 0
      ? []
      : toDetails(
          "Matériau",
          item.itemMaterial && getDentureMaterialLabel(item.itemMaterial.id)
        )),
    ...toDetails("Remplacement", item.isReplacement && "Oui"),
    ...toDetails("Options", getPartialDentureOptionLabels(item).join(", ")),
    ...extraDetails,
  ];
  const hookDetails = (item.partialDenture?.hooks ?? []).map(hook => ({
    label:
      hook.hooksPosition?.length > 0
        ? hook.hooksPosition.join(", ")
        : "Crochet",
    value: hook.hooksMaterial?.label ?? "",
  }));

  return [
    { title: `Prothèse${titleSuffix}`, details: prosthesisDetails },
    { title: `Crochets${titleSuffix}`, details: hookDetails },
    ...(archGroups.length > 0
      ? archGroups
      : [
          {
            title: `Esthétique${titleSuffix}`,
            details: getDentureAestheticsDetails(item, product),
          },
        ]),
  ];
};

const getDentureDetailGroups = (product: Product): ProductDetailGroup[] => {
  const items = product.removableItem ?? [];

  return items
    .flatMap((item, index) =>
      getDentureItemGroups(
        item,
        product,
        items.length > 1 ? ` · ${getArchFromTeeth(item.teeth ?? [])}` : "",
        index === 0 ? getPreferenceDetails(product) : []
      )
    )
    .filter(group => group.details.length > 0);
};

export const getProductSummary = (product: Product): string =>
  isDenture(product) ? getDentureSummary(product) : getItemsSummary(product);

export const getProductDetailGroups = (
  product: Product
): ProductDetailGroup[] =>
  isDenture(product)
    ? getDentureDetailGroups(product)
    : getItemsDetailGroups(product);
